/**
 * Inbox copy and tap targets for trip alerts.
 * Lock-screen taps open /updates/:id. href is the related trip action, not This phone.
 * Each category presents the facts that help on that screen, not a generic essay card.
 */

import type { AlertCategory, InboxAlert } from "./push.ts";
import type { Booking, Place, TripDocument, TripEvent } from "./schema.ts";
import { DateTime } from "luxon";
import { itemsForDate } from "./itinerary.ts";
import { formatMinor } from "./money.ts";
import { clockHm, formatDateLabel } from "./time.ts";
import { composeTravelDay, travelDayLabel } from "./travel-day.ts";
import { thailandArrivalFlight } from "./tdac.ts";
import { upcomingPayments, type UpcomingItem } from "./upcoming.ts";
import { TMD_WARNING_PAGE_URL } from "./warning-bulletins.ts";

export function inboxPath(alertId: string): string {
  return `/updates/${alertId}`;
}

export type AlertFact = { label: string; value: string };

export type AlertLayout =
  | "test"
  | "change"
  | "money"
  | "tdac"
  | "briefing"
  | "move"
  | "transfer"
  | "flight"
  | "pack"
  | "weather"
  | "warning"
  | "facts";

export type AlertHero =
  | { kind: "change"; from: string; to: string }
  | { kind: "amount"; value: string; caption: string }
  | { kind: "count"; value: string; caption: string }
  | { kind: "time"; value: string; caption: string }
  | { kind: "route"; from: string; to: string; fromTime: string | null; toTime: string | null }
  | null;

export type AlertStop = { time: string | null; title: string; href?: string };

export type AlertPresentation = {
  layout: AlertLayout;
  kicker: string;
  title: string;
  summary: string | null;
  honesty: string | null;
  facts: AlertFact[];
  hero: AlertHero;
  stops: AlertStop[];
  checklist: string[];
  meeting: string | null;
  dietary: string[];
  action: { label: string; href: string; external?: boolean } | null;
  secondary: { label: string; href: string } | null;
  confirmTest: boolean;
  showTdacChecklist: boolean;
  statusNote: string | null;
  when: string | null;
  source: string | null;
  sourceHref: string | null;
  preview: string;
};

const KICKER: Record<AlertCategory, string> = {
  test: "Test",
  briefing: "This morning",
  money_due: "Payment",
  checkout: "At the hotel",
  tdac: "Arrival card",
  pickup_reminder: "Pickup",
  pickup_change: "Pickup changed",
  missing_detail: "Still to confirm",
  tomorrow_move: "Tomorrow",
  food_tour: "Food tour",
  offline_pack: "Offline copy",
  flight_gate: "Gate",
  flight_disruption: "Flight",
  connection_concern: "Connection",
  arrival_transfer: "Pickup",
  rain_activity: "Weather",
  boat_crossing: "Boat",
  official_warning: "Official warning",
  itinerary: "Itinerary",
  next_destination: "Next stay",
  check_in_opens: "Check-in",
  budget_exhausted: "Owner note",
};

const HONESTY: Partial<Record<AlertCategory, string>> = {
  test: "Quiet hours do not apply to tests. A row in this inbox is not proof the lock screen showed it.",
  money_due: "An expected date is not proof the payment is overdue.",
  checkout: "Pay at the property is not a supplier bank transfer.",
  tdac: "Opening the official website is not completion.",
  pickup_change: "This app never messages the driver. Confirm with them yourself.",
  pickup_reminder: "Times on the plan stay a working estimate until the owner marks them confirmed.",
  flight_gate: "Airport displays remain the authority. Gate coverage is not guaranteed.",
  flight_disruption: "Booked times stay the baseline. Check the airline for what happens at the airport.",
  connection_concern: "A tighter gap is not a connection verdict. Contact the airline.",
  arrival_transfer: "This app never messages the driver or boat operator.",
  rain_activity: "A forecast overlap is a planning threshold, not a safety standard. Bookings are not moved automatically.",
  boat_crossing: "This is not a safe or unsafe verdict. Confirm service with the operator.",
  official_warning: "A bulletin we could not match to your dates is unknown, never an all-clear. An aviation notice is not automatically a warning on the ground.",
  budget_exhausted: "This is not a family travel alert. Booked times stay visible.",
  missing_detail: "Unknown facts stay unknown. This is a reminder to confirm, not a filled-in answer.",
  food_tour: "Dietary notes on the tour card are the record. This is not a guaranteed-safe meal.",
  offline_pack: "Browser storage can still be evicted. Map tiles are not in the pack.",
};

function clockish(value: string): string {
  const hm = clockHm(value);
  if (hm && /^\d{2}:\d{2}$/.test(hm)) return hm;
  return value;
}

export function alertWhenLabel(iso: string | null | undefined, now = new Date()): string | null {
  if (!iso) return null;
  const then = DateTime.fromISO(iso, { setZone: true });
  if (!then.isValid) return null;
  const mins = Math.max(0, Math.round((now.getTime() - then.toMillis()) / 60000));
  if (mins < 1) return "Just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "Yesterday" : `${days} days ago`;
}

function hrefId(href: string, kind: "transfer" | "flight" | "event" | "money" | "day" | "stay"): string | null {
  const map: Record<typeof kind, RegExp> = {
    transfer: /^\/journey\/transfer\/([^/?#]+)/,
    flight: /^\/journey\/flight\/([^/?#]+)/,
    event: /^\/journey\/event\/([^/?#]+)/,
    money: /^\/money\/([^/?#]+)/,
    day: /^\/journey\/day\/([^/?#]+)/,
    stay: /^\/journey\/stay\/([^/?#]+)/,
  };
  return map[kind].exec(href)?.[1] ?? null;
}

function layoutFor(category: AlertCategory, hasChange: boolean): AlertLayout {
  switch (category) {
    case "test":
      return "test";
    case "pickup_change":
    case "flight_gate":
      return "change";
    case "flight_disruption":
      return hasChange ? "change" : "flight";
    case "connection_concern":
      return hasChange ? "change" : "flight";
    case "money_due":
    case "checkout":
      return "money";
    case "tdac":
      return "tdac";
    case "briefing":
      return "briefing";
    case "tomorrow_move":
      return "move";
    case "pickup_reminder":
    case "arrival_transfer":
    case "boat_crossing":
      return "transfer";
    case "offline_pack":
      return "pack";
    case "rain_activity":
      return "weather";
    case "official_warning":
      return "warning";
    default:
      return "facts";
  }
}

export function tdacTravellerLabel(key: string, trip: TripDocument | null): string {
  const [group, rawIndex] = key.split(":");
  const index = Number(rawIndex);
  if (!trip || !Number.isFinite(index)) return key.replace(":", " ");
  if (group === "trip") return `Traveller ${index}`;
  const household = trip.households.find((row) => row.id === group);
  if (!household) return key.replace(":", " ");
  if (index <= household.adults) {
    return household.adults > 1 ? `${household.label} adult ${index}` : `${household.label} adult`;
  }
  const child = index - household.adults;
  return household.children > 1 ? `${household.label} child ${child}` : `${household.label} child`;
}

function moneyItem(alert: InboxAlert, trip: TripDocument | null): UpcomingItem | null {
  if (!trip) return null;
  const bookingId = hrefId(alert.href, "money");
  if (!bookingId) return null;
  const items = upcomingPayments(trip).filter((row) => row.bookingId === bookingId && !row.paid && row.amountMinor > 0);
  const household = alert.audience.householdIds.length === 1 ? alert.audience.householdIds[0] : null;
  if (household) {
    const hit = items.find((row) => row.householdId === household);
    if (hit) return hit;
  }
  return items[0] ?? null;
}

function transferFor(alert: InboxAlert, trip: TripDocument | null): TripEvent | null {
  if (!trip) return null;
  const id = hrefId(alert.href, "transfer");
  if (!id) return null;
  return trip.events.find((row) => row.id === id) ?? null;
}

function flightFor(alert: InboxAlert, trip: TripDocument | null) {
  if (!trip) return null;
  const id = hrefId(alert.href, "flight");
  if (!id) return null;
  return trip.flights.find((row) => row.id === id) ?? null;
}

function eventFor(alert: InboxAlert, trip: TripDocument | null): TripEvent | null {
  if (!trip) return null;
  const id = hrefId(alert.href, "event") ?? hrefId(alert.href, "transfer");
  if (!id) return null;
  return trip.events.find((row) => row.id === id) ?? null;
}

function bookingForEvent(event: TripEvent | null, trip: TripDocument | null): Booking | null {
  if (!event?.bookingId || !trip) return null;
  return trip.bookings.find((row) => row.id === event.bookingId) ?? null;
}

function placeName(trip: TripDocument | null, placeId: string | null | undefined): string | null {
  if (!trip || !placeId) return null;
  return trip.places.find((row) => row.id === placeId)?.name ?? null;
}

function destinationPlace(event: TripEvent | null, trip: TripDocument | null): Place | null {
  if (!event || !trip) return null;
  const id = event.toPlaceId ?? event.meetingPlaceId;
  if (!id) return null;
  return trip.places.find((row) => row.id === id) ?? null;
}

function connectionMinutes(body: string): { from: string; to: string } | null {
  const match = body.match(/from (\d+) to (\d+) minutes/i);
  if (!match) return null;
  return { from: `${match[1]} min`, to: `${match[2]} min` };
}

function actionFor(alert: InboxAlert): AlertPresentation["action"] {
  switch (alert.category) {
    case "test":
      return null;
    case "tdac":
      return { label: "Official TDAC portal", href: "https://tdac.immigration.go.th/", external: true };
    case "briefing":
      return { label: "Open Today", href: "/today" };
    case "money_due":
    case "checkout":
      return { label: "Open this payment", href: alert.href };
    case "offline_pack":
      return { label: "Download on this phone", href: "/essentials/phone#offline-pack" };
    case "official_warning": {
      const official = alert.sourceUrl && /^https?:\/\//i.test(alert.sourceUrl)
        ? { label: "Open the official bulletin", href: alert.sourceUrl, external: true }
        : { label: "Open Thai weather office", href: TMD_WARNING_PAGE_URL, external: true };
      return official;
    }
    case "budget_exhausted":
      return { label: "Owner status", href: "/essentials/phone" };
    case "missing_detail":
      return { label: "Open the gap", href: alert.href };
    case "pickup_change":
    case "pickup_reminder":
    case "arrival_transfer":
    case "boat_crossing":
      return { label: "Open the transfer", href: alert.href };
    case "flight_gate":
    case "flight_disruption":
    case "connection_concern":
      return { label: "Open the flight", href: alert.href };
    case "tomorrow_move":
      return { label: "Open tomorrow", href: alert.href };
    case "food_tour":
    case "rain_activity":
      return { label: "Open the plan", href: alert.href };
    default:
      if (alert.href.startsWith("/updates")) return null;
      return { label: "Open in the trip", href: alert.href };
  }
}

function secondaryFor(alert: InboxAlert, trip: TripDocument | null): AlertPresentation["secondary"] {
  if (alert.category === "official_warning") {
    if (alert.href.startsWith("/journey") || alert.href.startsWith("/today")) {
      return { label: "Open the trip plan", href: alert.href };
    }
    return { label: "Owner status", href: "/essentials/phone" };
  }
  if (alert.category === "test") return { label: "Alert settings", href: "/essentials/phone" };
  if (alert.category === "tdac") {
    const arrival = trip ? thailandArrivalFlight(trip) : null;
    return {
      label: "Arrival flight",
      href: arrival ? `/journey/flight/${arrival.id}` : (alert.href.startsWith("/journey") ? alert.href : "/journey"),
    };
  }
  if (alert.category === "flight_disruption" || alert.category === "flight_gate" || alert.category === "connection_concern") {
    return { label: "All flights", href: "/bookings" };
  }
  if (alert.category === "food_tour") {
    return { label: "Allergy cards", href: "/essentials/cards" };
  }
  const event = transferFor(alert, trip) ?? eventFor(alert, trip);
  const dest = destinationPlace(event, trip);
  if (dest && (alert.category === "pickup_change" || alert.category === "pickup_reminder" || alert.category === "arrival_transfer")) {
    return { label: "Show the driver", href: `/show/driver/${dest.id}` };
  }
  return null;
}

function heroFor(
  alert: InboxAlert,
  trip: TripDocument | null,
  change: { from: string; to: string } | null,
): AlertHero {
  if (alert.category === "connection_concern" && !change) {
    const mins = connectionMinutes(alert.body);
    if (mins) return { kind: "change", from: mins.from, to: mins.to };
  }
  if (change) return { kind: "change", from: change.from, to: change.to };

  if (alert.category === "money_due" || alert.category === "checkout") {
    const item = moneyItem(alert, trip);
    if (item) {
      const certainty = item.group === "confirmed_deadline" || item.hardDeadline
        ? "Confirmed deadline"
        : item.group === "pay_at_property"
          ? "Pay at the property"
          : "Expected date";
      return { kind: "amount", value: formatMinor(item.currency, item.amountMinor), caption: certainty };
    }
  }

  if (alert.category === "tdac") {
    const match = alert.body.match(/^(\d+) traveller/);
    if (match) {
      const n = Number(match[1]);
      return {
        kind: "count",
        value: String(n),
        caption: n === 1 ? "traveller still needs a card" : "travellers still need a card",
      };
    }
  }

  if (alert.category === "pickup_reminder") {
    const event = transferFor(alert, trip);
    const time = event ? clockish(event.startLocal ?? event.plannedDeparture ?? alert.applicableAt ?? "") : clockish(alert.applicableAt ?? "");
    if (time) return { kind: "time", value: time, caption: "On the plan" };
  }

  const flight = flightFor(alert, trip);
  if (flight && (alert.category === "flight_disruption" || alert.category === "flight_gate" || alert.category === "connection_concern")) {
    return {
      kind: "route",
      from: flight.departure.airport,
      to: flight.arrival.airport,
      fromTime: clockHm(flight.departure.local),
      toTime: clockHm(flight.arrival.local),
    };
  }

  return null;
}

function briefingStops(alert: InboxAlert, trip: TripDocument | null): AlertStop[] {
  if (!trip || alert.category !== "briefing") return [];
  const date = alert.eventLabel && /^\d{4}-\d{2}-\d{2}$/.test(alert.eventLabel) ? alert.eventLabel : null;
  if (!date) return [];
  return itemsForDate(trip, date).slice(0, 6).map((item) => ({
    time: item.timeLabel,
    title: item.title,
    href: item.href,
  }));
}

function moveStops(alert: InboxAlert, trip: TripDocument | null): AlertStop[] {
  if (!trip || alert.category !== "tomorrow_move") return [];
  const date = hrefId(alert.href, "day");
  if (!date) return [];
  return composeTravelDay(trip, date).map((stage) => ({
    time: stage.timeLabel,
    title: stage.title,
    href: stage.href,
  }));
}

function factsFor(
  alert: InboxAlert,
  trip: TripDocument | null,
  layout: AlertLayout,
  hero: AlertHero,
): AlertFact[] {
  const facts: AlertFact[] = [];
  const event = transferFor(alert, trip) ?? eventFor(alert, trip);
  const flight = flightFor(alert, trip);
  const money = moneyItem(alert, trip);

  if (layout === "test" || layout === "briefing") return facts;

  if (layout === "money" && money) {
    facts.push({ label: "Booking", value: money.title });
    if (money.householdLabel) facts.push({ label: "Household", value: money.householdLabel });
    if (money.display) facts.push({ label: "When", value: money.display });
    else if (money.date) facts.push({ label: "When", value: formatDateLabel(money.date) });
    return facts;
  }

  if (layout === "tdac" && trip) {
    const arrival = thailandArrivalFlight(trip);
    if (arrival) {
      facts.push({ label: "Arrival flight", value: arrival.number });
      facts.push({ label: "Lands", value: `${arrival.arrival.airport} ${clockish(arrival.arrival.local)}` });
      facts.push({ label: "Date", value: formatDateLabel(arrival.arrival.local.slice(0, 10)) });
    }
    return facts;
  }

  if (layout === "transfer" && event) {
    const from = placeName(trip, event.fromPlaceId) ?? event.fromAirport;
    const to = placeName(trip, event.toPlaceId);
    if (from) facts.push({ label: "From", value: from });
    if (to) facts.push({ label: "To", value: to });
    const time = event.startLocal ?? event.plannedDeparture;
    if (time && hero?.kind !== "time" && hero?.kind !== "change") {
      facts.push({ label: "On the plan", value: clockish(time) });
    }
    if (event.pickupMode === "when_ready") facts.push({ label: "Mode", value: "When ready, not a fixed appointment" });
    if (event.date) facts.push({ label: "Date", value: formatDateLabel(event.date) });
    return facts;
  }

  if (layout === "change" && event) {
    const from = placeName(trip, event.fromPlaceId) ?? event.fromAirport;
    const to = placeName(trip, event.toPlaceId);
    if (from) facts.push({ label: "From", value: from });
    if (to) facts.push({ label: "To", value: to });
    if (event.date) facts.push({ label: "Date", value: formatDateLabel(event.date) });
  }

  if ((layout === "flight" || layout === "change") && flight) {
    facts.push({ label: "Flight", value: flight.number });
    if (hero?.kind !== "route") {
      facts.push({ label: "Route", value: `${flight.departure.airport} to ${flight.arrival.airport}` });
      facts.push({ label: "Booked departure", value: clockish(flight.departure.local) });
    }
    if (flight.departure.terminal) facts.push({ label: "Terminal", value: flight.departure.terminal });
  }

  if (layout === "move" && trip) {
    const date = hrefId(alert.href, "day");
    if (date) facts.push({ label: "Day", value: travelDayLabel(trip, date) });
  }

  if (layout === "warning") {
    if (alert.eventLabel) facts.push({ label: "Bulletin", value: alert.eventLabel });
    if (alert.destinationLabel) facts.push({ label: "Trip place", value: alert.destinationLabel });
    facts.push({
      label: "Applies to",
      value: alert.audience.ownerStatusOnly
        ? "Not matched to a region or valid time yet"
        : (alert.destinationLabel ?? "This trip"),
    });
    if (alert.applicableAt) {
      const until = DateTime.fromISO(alert.applicableAt, { setZone: true });
      facts.push({
        label: "Valid until",
        value: until.isValid ? until.toFormat("ccc d LLL HH:mm") : clockish(alert.applicableAt),
      });
    }
    facts.push({ label: "Issued by", value: "Thai Meteorological Department" });
    return facts;
  }

  if (layout === "facts" || layout === "weather" || layout === "pack") {
    if (alert.eventLabel && alert.eventLabel !== alert.title) facts.push({ label: "About", value: alert.eventLabel });
    if (alert.destinationLabel) facts.push({ label: "Place", value: alert.destinationLabel });
    if (alert.applicableAt && hero?.kind !== "time" && hero?.kind !== "change") {
      facts.push({ label: "When", value: clockish(alert.applicableAt) });
    }
  }

  if (alert.category === "food_tour" && event) {
    const time = event.startLocal ?? event.plannedDeparture;
    if (time) facts.push({ label: "Meeting time", value: clockish(time) });
    const meetingPlace = placeName(trip, event.meetingPlaceId ?? event.toPlaceId);
    if (meetingPlace) facts.push({ label: "Meeting", value: meetingPlace });
    if (event.meetingNote) facts.push({ label: "Note", value: event.meetingNote });
    if (event.startConfidence?.includes("provisional") || event.timeConfidence?.includes("provisional")) {
      facts.push({ label: "Timing", value: "Provisional until the operator confirms" });
    }
  }

  return facts;
}

function summaryFor(alert: InboxAlert, layout: AlertLayout, hero: AlertHero): string | null {
  if (layout === "test") return alert.body;
  if (layout === "briefing") return null;
  if (layout === "change") {
    if (alert.category === "pickup_change") return "Confirm the new details with the driver yourself.";
    if (alert.category === "flight_gate") return "Confirm the gate on airport displays before you walk.";
    if (alert.category === "connection_concern") return "Contact the airline. This is not a missed-connection verdict.";
    return "Booked times stay the baseline until you check the airline.";
  }
  if (layout === "money") return null;
  if (layout === "tdac") return "Submit on the official immigration portal. Opening the site is not completion.";
  if (layout === "transfer") {
    if (alert.category === "boat_crossing") return "Confirm service with the operator. This is not a safe or unsafe verdict.";
    if (alert.category === "arrival_transfer") return "The inbound flight changed. Confirm the pickup with the operator.";
    return "Open the transfer for driver and meeting details.";
  }
  if (layout === "move") return "Hotel or transport changes tomorrow. Use the combined list below.";
  if (layout === "pack") return "A flight or low-signal day is close, and this phone’s offline copy is missing or stale.";
  if (layout === "warning") {
    return alert.audience.ownerStatusOnly
      ? "Read the official bulletin and decide whether it applies before telling the family."
      : "Open the official bulletin. This app does not move bookings automatically.";
  }
  if (layout === "flight" && hero?.kind === "route") return alert.body.split(".")[0] ?? alert.body;
  return alert.body;
}

function previewFor(alert: InboxAlert, hero: AlertHero, stops: AlertStop[]): string {
  if (hero?.kind === "change") return `${hero.from} to ${hero.to}`;
  if (hero?.kind === "amount") return hero.value;
  if (hero?.kind === "count") return `${hero.value} ${hero.caption}`;
  if (hero?.kind === "time") return hero.value;
  if (hero?.kind === "route") return `${hero.from} to ${hero.to}`;
  if (alert.category === "test") return "Confirm you saw the lock-screen banner";
  if (alert.category === "official_warning" && alert.eventLabel) return alert.eventLabel;
  if (stops[0]) return stops[0].time ? `${stops[0].time} ${stops[0].title}` : stops[0].title;
  const first = alert.body.split(".")[0] ?? alert.body;
  return first.length > 88 ? `${first.slice(0, 85).trim()}…` : first;
}

export function presentAlert(
  alert: InboxAlert,
  trip: TripDocument | null,
  now = new Date(),
): AlertPresentation {
  const expired = Boolean(alert.expiresAt && Date.parse(alert.expiresAt) < now.getTime());
  const change = alert.changeFrom && alert.changeTo
    ? { from: clockish(alert.changeFrom), to: clockish(alert.changeTo) }
    : alert.category === "connection_concern"
      ? connectionMinutes(alert.body)
      : null;
  const layout = layoutFor(alert.category, Boolean(change));
  const hero = heroFor(alert, trip, change);
  const event = transferFor(alert, trip) ?? eventFor(alert, trip);
  const booking = bookingForEvent(event, trip);
  const stops = layout === "briefing" ? briefingStops(alert, trip) : layout === "move" ? moveStops(alert, trip) : [];
  const checklist = layout === "move"
    ? ["Bags packed tonight", "Confirmed departure time", "Documents and boarding passes", "Offline copy on this phone"]
    : layout === "warning"
      ? alert.audience.ownerStatusOnly
        ? [
            "Read what the weather office posted",
            alert.destinationLabel
              ? `See if it covers ${alert.destinationLabel} and the trip dates`
              : "See if it covers the trip places and dates",
            "Only then tell the family if it applies",
          ]
        : [
            "Read the official bulletin",
            alert.destinationLabel
              ? `Check outdoor plans in ${alert.destinationLabel}`
              : "Check outdoor plans on the trip",
            "This app does not move bookings",
          ]
      : [];
  const meeting = event?.pickupInstruction
    ?? event?.meetingNote
    ?? null;
  const dietary = booking?.dietaryNotes ?? [];
  const facts = factsFor(alert, trip, layout, hero);
  if (layout === "briefing" && alert.destinationLabel) {
    facts.push({ label: "Tonight", value: alert.destinationLabel });
  }

  let statusNote: string | null = null;
  if (alert.status === "superseded") statusNote = "Replaced by a later update.";
  if (alert.status === "resolved") statusNote = alert.resolutionLabel ?? "Resolved.";
  if (expired) statusNote = "This update has expired. Open the live trip card for current facts.";

  return {
    layout,
    kicker: alert.category === "official_warning" && alert.audience.ownerStatusOnly
      ? "Owner review"
      : KICKER[alert.category],
    title: alert.title,
    summary: summaryFor(alert, layout, hero),
    honesty: alert.category === "official_warning" && !alert.audience.ownerStatusOnly
      ? "Official TMD warning. This app does not move bookings."
      : HONESTY[alert.category] ?? null,
    facts,
    hero,
    stops,
    checklist,
    meeting,
    dietary,
    action: actionFor(alert),
    secondary: secondaryFor(alert, trip),
    confirmTest: alert.category === "test" && alert.status === "active",
    showTdacChecklist: alert.category === "tdac",
    statusNote,
    when: alertWhenLabel(alert.freshnessAt, now),
    source: layout === "test" ? null : alert.source,
    sourceHref: layout === "test" ? null : (alert.sourceUrl ?? null),
    preview: previewFor(alert, hero, stops),
  };
}

export function listLine(alert: InboxAlert, trip: TripDocument | null = null): string {
  return presentAlert(alert, trip).preview;
}
